import { WarningDescriptionOptions, WarningLevel, WarningType } from "../WarningDescription.js";
export type SystemRelevancy = "unknown" | "error";

/**
 * The de-facto warning variant. Every warning has a variant; when no specific sub-form
 * applies, the variant is `BASE_WARNING_VARIANT`. This is the schema-side default and is
 * always an accepted value for every warning type — it does not appear in any type's
 * `variants` list (handlers either pass `BASE_WARNING_VARIANT` or one of the listed
 * specific variants).
 *
 * To force callers to pick an explicit variant, define a new warning type whose handler
 * never passes `BASE_WARNING_VARIANT` — the schema still accepts it, but the handler
 * contract makes it unreachable.
 */
export const BASE_WARNING_VARIANT = "BASE" as const;

// Plumbing/sensor --> Device --> Network --> Verdi
export type ExtendedWarningDescription = WarningDescriptionOptions & {
    alertName: string;
    //Literally no idea what this does
    hideFromConfig?: boolean;
    /** When true, this warning type can be configured for SMS alerts in the dashboard. */
    smsEnabled?: boolean;
    /**
     * Whether this warning type is offered for notification subscriptions. Required and stated
     * explicitly on every type — there is no default to infer from, so adding a warning type forces the
     * call. `false` marks lifecycle, diagnostic and internal-only states a grower can't act on from a
     * text message.
     *
     * UI-only by design: it filters what the dashboard's warning pickers present, nothing more. There
     * is no server-side enforcement — the notification pipeline sends for any warning type present on
     * a subscription, including excluded ones written directly to the database.
     *
     * Distinct from the pre-existing `smsEnabled`, which gates the legacy SMS-alerts settings card while
     * this one gates the notification-subscription picker; the two disagree on some types, which stands
     * until the surfaces are consolidated.
     */
    supportsNotifications: boolean;
    simpleFix?: string;
    longDescription?: string;
    relevantSystems?: Partial<{
        sensor: SystemRelevancy;
        plumbing: SystemRelevancy;
        device: SystemRelevancy;
        networkConnection: SystemRelevancy;
        network: SystemRelevancy;
        verdi: SystemRelevancy;
    }>;
    /**
     * The set of warning levels that this warning type can be raised at.
     *
     * Use this to understand what severity levels are possible for a given warning type,
     * and to validate that a warning being posted uses a compatible level.
     *
     * @example [WarningLevel.WARNING, WarningLevel.ERROR] — warning can be either level 1 or 2
     */
    possibleWarningLevels: readonly WarningLevel[];

    /**
     * Specific (non-BASE) variants this type supports. Every type implicitly also accepts
     * `BASE_WARNING_VARIANT` — do not list it here. Use `[]` for types that only ever
     * use BASE.
     *
     * Handlers must always supply `warningVariant` explicitly: either
     * `BASE_WARNING_VARIANT` or one of the values listed here. The schema validator
     * rejects any other value.
     */
    variants: readonly string[];

    /**
     * The problem keys this warning type may attach to a posted warning's
     * `warningProblems` list. Each key is the catalog identifier for one specific
     * cause/contributing-factor that the warning may indicate (e.g. `dead-battery`,
     * `system-outage`). Use `[]` for types that don't enumerate problems yet.
     *
     * The schema validator rejects any `warningProblems[*].problemKey` not listed
     * here. Adding a new key requires a matching entry in every locale's
     * warnings catalog (`problems.<problemKey>`); the catalog test enforces both
     * directions.
     */
    possibleProblemKeys: readonly string[];

    /**
     * The fix keys this warning type may attach to a posted warning's `warningFixes`
     * list. Each key is the catalog identifier for one specific remediation step the
     * warning may suggest (e.g. `check-gateway-and-range`, `fix-device-settings`). Use
     * `[]` for types that don't enumerate fixes yet.
     *
     * The handler selects which of these apply at runtime; the schema validator rejects
     * any `warningFixes[*].fixKey` not listed here. Adding a new key requires a matching
     * entry in every locale's warnings catalog (`fixes.<fixKey>`); the catalog test
     * enforces both directions.
     */
    possibleFixKeys: readonly string[];
};
export const standardWarningDescriptions = {
    //Root causes, always the root cause
    offline: {
        details: "Device cannot be contacted",
        simpleFix:
            "Check that the gateway is online, the device is within range, and the battery has charge (replace it if needed).",
        longDescription:
            `A device will be determined to be offline when it has not been heard from in 4 hours. ` +
            `This can be caused by a gateway or network failure or a device being out of range. ` +
            `\nIf you notice a large number of offline devices, it is likely that a gateway has gone down or a ` +
            `network outage has occurred. Consider checking that gateways are connected to power and solar panels ` +
            `are not blocked by foliage. If this is a network outage, you may need to wait for the outage to be resolved. ` +
            `\nIf this is the only offline device, and it goes offline frequently, or you often see other offline ` +
            `devices in the area, this device may be in a network dead zone. ` +
            `Adding an additional gateway or adjusting the positions of existing gateways may help. ` +
            `\nIf this doesn't work, ensure the antenna is firmly attached to the device and has the clearest possible ` +
            `view of the gateway.` +
            `CAUTION: overtightening the antenna or using a wrench may permanently damage the device. Finger tight is fine ` +
            `\nIf none of the above helps ensure the device has power. ` +
            `If the device has a screw top and 3 buttons, try manually opening the valve. ` +
            `If the device has no screw cap, try using a magnet to engage the manual override on the device. ` +
            `If the device is not responsive then it is likely the battery is dead and needs replacing. ` +
            `\nIf the device remains unresponsive after replacing the battery, the device may have suffered damage. ` +
            `Please put a label on the device and write down anything that might help us determine the problem. ` +
            `Then contact the Verdi team and include the ID of the failed device and the circumstances of it's failure. ` +
            `We will ensure the device is replaced promptly.`,
        priority: 999,
        alertName: "offline device",
        smsEnabled: true,
        supportsNotifications: true,
        relevantSystems: {
            plumbing: "unknown",
            device: "unknown",
            networkConnection: "error",
        },
        possibleWarningLevels: [WarningLevel.ERROR],
        // `gateway` is raised on the gateway itself, where the base copy's "check the
        // gateway and the device's range" advice doesn't apply.
        variants: ["gateway"],
        // Exemplar for the new `warningProblems` infrastructure: an offline device
        // can be offline for several enumerable reasons, and surfacing them as a
        // bulleted list is useful when the cause isn't obvious. Handlers can attach
        // any subset of these to a posted warning. Keep this list aligned with
        // `problems.*` entries in every locale's warnings catalog.
        possibleProblemKeys: ["dead-battery", "nearby-gateway-offline", "system-outage"],
        possibleFixKeys: ["check-gateway-and-range", "check-gateway-power"],
    },
    "waiting for device": {
        details: "Waiting for device to contact the Verdi network",
        simpleFix: "Ensure gateway is online and device is in range",
        priority: 998,
        alertName: "waiting for device",
        supportsNotifications: false,
        relevantSystems: {
            plumbing: "unknown",
            device: "unknown",
            networkConnection: "error",
        },
        possibleWarningLevels: [WarningLevel.WARNING],
        variants: [],
        possibleProblemKeys: [],
        possibleFixKeys: ["check-gateway-and-range"],
    },

    "misconfigured device": {
        alertName: "Device misconfigured",
        details: "Device not correctly set up",
        simpleFix: "Fix device settings",
        priority: 980,
        hideFromConfig: false,
        supportsNotifications: false,
        relevantSystems: {
            verdi: "error",
        },
        // Both WARNING and ERROR are valid: current misconfiguration checks always produce severity 2
        // (ERROR), but the recalledVersions mechanism may use either allowed WarningLevel for this
        // warning type, so WARNING is intentionally kept available for future use.
        possibleWarningLevels: [WarningLevel.WARNING, WarningLevel.ERROR],
        // Each variant identifies a specific misconfiguration check failure. The handler
        // currently uses a first-failure waterfall and supplies the matching variant; once
        // collect-all behaviour lands (with `warningProblems`), a multi-failure case will
        // use `BASE_WARNING_VARIANT` for `warningVariant` and list each failure as a
        // problem instead.
        variants: [
            "invalid-device-id",
            "invalid-device-type",
            "incompatible-firmware",
            "invalid-zone-config",
            "invalid-sensor-parameters",
            "indistinguishable-sensors",
        ],
        possibleProblemKeys: [],
        // One fix per failed check: the handler maps each variant to its fix, with
        // `fix-device-settings` as the fallback for unmapped (recalled-version) failures.
        possibleFixKeys: [
            "fix-device-settings",
            "report-to-verdi",
            "review-zone-config",
            "review-sensor-parameters",
            "differentiate-sensors",
        ],
    },
    "Manual Override Engaged": {
        details: "Device is in manual operating mode and will not follow the irrigation schedule",
        simpleFix: `To return to scheduled irrigation, tap on the device, scroll to its "operating mode", and set it to "Sync to schedule." Changes take effect after the next successful contact with the device.`,
        priority: 975,
        alertName: "Manual Override Engaged",
        supportsNotifications: true,
        relevantSystems: {
            device: "unknown",
        },
        // INFO is the current intended severity, but ERROR remains allowed temporarily so
        // existing persisted warnings at the legacy level can still validate if re-saved.
        possibleWarningLevels: [WarningLevel.INFO, WarningLevel.ERROR],
        variants: ["manual-open", "manual-closed"],
        possibleProblemKeys: [],
        possibleFixKeys: ["sync-to-schedule"],
    },
    "testing network": {
        details: "Conducting a network test",
        simpleFix: "Please wait for the test to conclude",
        priority: 970,
        alertName: "network test",
        supportsNotifications: false,
        relevantSystems: {
            plumbing: "unknown",
            device: "unknown",
            networkConnection: "unknown",
        },
        possibleWarningLevels: [WarningLevel.WARNING],
        variants: [],
        possibleProblemKeys: [],
        possibleFixKeys: ["wait-for-test"],
    },
    "configuring sensor": {
        details: "The sensor is being configured",
        simpleFix: "Please wait for configuration to complete. Graphs and device status may not be correct until then.",
        priority: 968,
        alertName: "configuring sensor",
        supportsNotifications: false,
        relevantSystems: {
            sensor: "unknown",
            device: "unknown",
        },
        possibleWarningLevels: [WarningLevel.INFO],
        variants: [],
        possibleProblemKeys: [],
        possibleFixKeys: [],
    },
    //High level things that can be actioned
    "damaged tensiometer": {
        details: "Negative pressure reading on tensiometer",
        simpleFix: "Follow instructions to refill tensiometer",
        priority: 851,
        alertName: "damaged tensiometer",
        smsEnabled: true,
        supportsNotifications: true,
        relevantSystems: {
            sensor: "error",
        },
        possibleWarningLevels: [WarningLevel.ERROR],
        variants: [],
        possibleProblemKeys: [],
        possibleFixKeys: ["refill-tensiometer"],
    },
    "poor connection": {
        details: "Too many messages between the device and gateway are being lost",
        simpleFix: "Ensure gateway is online and device is in range. Warning may take up to 48 hours to disappear",
        priority: 850,
        alertName: "poor device connection",
        smsEnabled: true,
        supportsNotifications: true,
        relevantSystems: {
            networkConnection: "error",
        },
        possibleWarningLevels: [WarningLevel.WARNING, WarningLevel.ERROR],
        variants: [],
        possibleProblemKeys: [],
        possibleFixKeys: ["check-gateway-and-range"],
    },
    "no network data": {
        alertName: "No network data",
        details: "Verdi isn't receiving enough data from this device to measure its connection quality",
        simpleFix: "Ensure gateway is online and device is in range. Warning may take up to 48 hours to disappear",
        priority: 749,
        hideFromConfig: true,
        supportsNotifications: true,
        relevantSystems: {
            networkConnection: "error",
        },
        possibleWarningLevels: [WarningLevel.ERROR],
        variants: [],
        possibleProblemKeys: [],
        possibleFixKeys: ["check-gateway-and-range"],
    },

    //Things which could be symptomatic, but could also cause warnings.
    "frequent resets": {
        details: "Device resetting frequently",
        simpleFix:
            "If you have not reset the device recently (either with a magnet or by hitting the buttons), report this " +
            "to your contact at Verdi",
        priority: 460,
        alertName: "excessive resets",
        smsEnabled: true,
        supportsNotifications: false,
        relevantSystems: {
            device: "unknown",
        },
        possibleWarningLevels: [WarningLevel.WARNING, WarningLevel.ERROR],
        variants: [],
        possibleProblemKeys: [],
        possibleFixKeys: ["report-to-verdi"],
    },
    "RTC sync error": {
        details: "Clock may be out of sync",
        simpleFix:
            "If you have not reset the device recently (either with a magnet or by hitting the buttons), report this " +
            "to your contact at Verdi. This is usually resolved by leaving the device alone for 48 hours.",
        priority: 452,
        alertName: "RTC Sync Warning",
        hideFromConfig: true,
        supportsNotifications: false,
        relevantSystems: {
            device: "unknown",
        },
        possibleWarningLevels: [WarningLevel.WARNING],
        variants: [],
        possibleProblemKeys: [],
        possibleFixKeys: ["report-and-wait"],
    },

    //Mostly symptoms of other root problems.
    "low accuracy": {
        details: "Device not correctly following schedules",
        simpleFix:
            "Ensure device remains online and avoid making last minute schedule adjustments. " +
            "This warning should dissipate in ~48 hours",
        priority: 250,
        alertName: "irrigating improperly",
        smsEnabled: true,
        supportsNotifications: false,
        relevantSystems: {
            device: "unknown",
        },
        possibleWarningLevels: [WarningLevel.WARNING],
        variants: [],
        possibleProblemKeys: [],
        possibleFixKeys: ["stay-online-avoid-last-minute-changes"],
    },
    "high sensor reading": {
        details: "Sensor is reading above threshold",
        simpleFix:
            "This sensor is reading above a warning threshold you set. " +
            "You can remove this threshold in the sensor settings",
        priority: 150,
        alertName: "sensor reading above threshold",
        smsEnabled: true,
        supportsNotifications: true,
        possibleWarningLevels: [WarningLevel.WARNING],
        variants: [],
        possibleProblemKeys: [],
        possibleFixKeys: ["adjust-sensor-threshold"],
    },
    "low sensor reading": {
        details: "Sensor is reading below threshold",
        simpleFix:
            "This sensor is reading below a warning threshold you set. " +
            "You can remove this threshold in the sensor settings",
        priority: 149,
        alertName: "sensor reading below threshold",
        smsEnabled: true,
        supportsNotifications: true,
        possibleWarningLevels: [WarningLevel.WARNING],
        variants: [],
        possibleProblemKeys: [],
        possibleFixKeys: ["adjust-sensor-threshold"],
    },

    "over watering": {
        details: "Device is irrigating more than expected",
        simpleFix:
            "Open Settings > Over Water Protection to review this threshold. You can disable OWP or adjust the limit.",
        priority: 130,
        alertName: "over watering",
        smsEnabled: true,
        supportsNotifications: true,
        possibleWarningLevels: [WarningLevel.WARNING],
        variants: ["single-irrigation-limit-exceeded", "weekly-irrigation-limit-exceeded"],
        possibleProblemKeys: [],
        possibleFixKeys: ["review-owp-settings"],
    },
    "under watering": {
        details: "Device hasn't irrigated in a long time",
        simpleFix:
            "The device is irrigating less than the minimum set in Over Water Protection. Open Settings > Over Water Protection to review this threshold. You can disable OWP or adjust the minimum.",
        priority: 129,
        alertName: "under watering",
        smsEnabled: true,
        supportsNotifications: true,
        possibleWarningLevels: [WarningLevel.WARNING],
        variants: [],
        possibleProblemKeys: [],
        possibleFixKeys: ["review-owp-settings"],
    },

    //Symptoms
    "invalid sensor reading": {
        details: "Sensor is reporting a value outside the physically possible range",
        simpleFix:
            "Check that the sensor is properly connected, undamaged, and correctly configured. " +
            "If the reading persists, the sensor may need to be replaced.",
        priority: 95,
        alertName: "invalid sensor reading",
        supportsNotifications: true,
        relevantSystems: {
            sensor: "error",
        },
        possibleWarningLevels: [WarningLevel.ERROR],
        variants: [],
        possibleProblemKeys: [],
        possibleFixKeys: ["check-sensor-connection"],
    },
    "zero sensor reading": {
        details: "Sensor may not be connected",
        simpleFix:
            "Ensure sensor is properly connected, installed, and not damaged. This warning can take up to 6 hours to dissipate.",
        priority: 90,
        alertName: "sensor disconnected/broken",
        smsEnabled: true,
        supportsNotifications: true,
        relevantSystems: {
            sensor: "error",
        },
        possibleWarningLevels: [WarningLevel.ERROR],
        variants: [],
        possibleProblemKeys: [],
        possibleFixKeys: ["check-sensor-connection"],
    },
    "failed verification": {
        details: "Verification failed",
        simpleFix:
            "Ensure water is available to the valve, the device is online, and avoid last minute schedule changes. " +
            "This warning will dissipate when an irrigation is executed successfully.",
        priority: 30,
        alertName: "failed verification",
        smsEnabled: true,
        supportsNotifications: true,
        relevantSystems: {
            plumbing: "error",
        },
        possibleWarningLevels: [WarningLevel.ERROR],
        // Single valve-state failures map to a state-specific variant; multi-state
        // failures use `multiple-failures`. Per-check details (timing, solenoid, sensor)
        // arrive as `warningProblems` in a follow-up PR.
        variants: ["scheduled-open", "scheduled-close", "manual-open", "manual-close", "multiple-failures"],
        possibleProblemKeys: [],
        possibleFixKeys: ["ensure-water-and-online"],
    },
} as const satisfies Record<WarningType, ExtendedWarningDescription>;

/**
 * The accepted `warningVariant` values for a given `warningType` literal:
 *   `BASE_WARNING_VARIANT` ∪ the type's specific `variants` from `standardWarningDescriptions`.
 *
 * Derives directly from the `as const` config so adding or removing a variant in the
 * config table updates every consumer's accepted type with no extra wiring. Used as the
 * `warningVariant` field type on the discriminated `PostWarningInfo` so handlers
 * type-check at the call site instead of relying solely on the schema validator at save
 * time.
 *
 * Examples:
 *   `WarningVariant<"offline">`            \u2192 `"BASE"`
 *   `WarningVariant<"Manual Override Engaged">` \u2192 `"BASE" | "manual-open" | "manual-closed"`
 *   `WarningVariant<WarningType>`           \u2192 `"BASE" | "manual-open" | ... | "multiple-failures"`
 */
export type WarningVariant<T extends WarningType = WarningType> = T extends WarningType
    ? typeof BASE_WARNING_VARIANT | (typeof standardWarningDescriptions)[T]["variants"][number]
    : never;

/**
 * The accepted `problemKey` values for an entry in a warning's `warningProblems`
 * list, derived from `standardWarningDescriptions[T].possibleProblemKeys`. A type with
 * no enumerated problems resolves to `never`, making it a compile error to attach
 * any problem to that warning type at all.
 *
 * Examples:
 *   `WarningProblemKey<"offline">`  → `"dead-battery" | "nearby-gateway-offline" | ...`
 *   `WarningProblemKey<"poor connection">` → `never`
 */
export type WarningProblemKey<T extends WarningType = WarningType> = T extends WarningType
    ? (typeof standardWarningDescriptions)[T]["possibleProblemKeys"][number]
    : never;

/**
 * Runtime shape of a single entry in a warning's `warningProblems` list. `problemKey`
 * is the catalog identifier (validated against `possibleProblemKeys` at save time);
 * `problemParams` are interpolation values for the problem's translation template,
 * subject to the same i18next reserved-key rules as `warningParams`.
 */
export type WarningProblem<T extends WarningType = WarningType> = {
    problemKey: WarningProblemKey<T>;
    problemParams?: Record<string, string | number>;
    defaultDisplayText?: string;
};

/**
 * The accepted `fixKey` values for an entry in a warning's `warningFixes` list,
 * derived from `standardWarningDescriptions[T].possibleFixKeys`. A type with no
 * enumerated fixes resolves to `never`, making it a compile error to attach any fix
 * to that warning type at all.
 *
 * Examples:
 *   `WarningFixKey<"offline">`  → `"check-gateway-and-range"`
 *   `WarningFixKey<"testing network">` → `"wait-for-test"`
 */
export type WarningFixKey<T extends WarningType = WarningType> = T extends WarningType
    ? (typeof standardWarningDescriptions)[T]["possibleFixKeys"][number]
    : never;

/**
 * Runtime shape of a single entry in a warning's `warningFixes` list. `fixKey` is the
 * catalog identifier (validated against `possibleFixKeys` at save time); `fixParams`
 * are interpolation values for the fix's translation template, subject to the same
 * i18next reserved-key rules as `warningParams`.
 */
export type WarningFix<T extends WarningType = WarningType> = {
    fixKey: WarningFixKey<T>;
    fixParams?: Record<string, string | number>;
    defaultDisplayText?: string;
};
